import { t } from "@lingui/core/macro";
import { EMPTY_STRING } from "@regrello/core-utils";
import type { FieldInstanceFields } from "@regrello/graphql-api";
import {
  type RegrelloDataGridV2EditableCellProps,
  useDataGridV2EditableCell,
  useDataGridV2FirstKeystroke,
} from "@regrello/ui-core";
import { useCallback, useRef, useState } from "react";
import { isWithinElement } from "../../../../../../../utils/isWithinElement";
import { useToastMessageQueue } from "../../../../../components/toast/useToastMessageQueue";
import { RegrelloFormFieldPhone } from "../../../../formFields/RegrelloFormFieldPhone";
import type { PhoneFieldPluginFrontendValue } from "./types";

export interface RegrelloEditableCellPhoneProps {
  /** A function to clear the value if it is invalid. */
  clearValueIfInvalid: (value: PhoneFieldPluginFrontendValue) => PhoneFieldPluginFrontendValue;

  /** The field instance that corresponds to the current table column. */
  fieldInstance: FieldInstanceFields;

  /** The callback to invoke when the user changes the value of the cell. */
  onValueChange: (value: PhoneFieldPluginFrontendValue) => void;

  /**
   * The current value to display in the cell. This is the value before editing and the value that
   * will be reinstated if editing is canceled.
   */
  value: PhoneFieldPluginFrontendValue;
}

/**
 * An editable cell in which the user can input a phone number. Intended for use in
 * `RegrelloDataGridV2`.
 */
export function RegrelloEditableCellPhone({
  api,
  clearValueIfInvalid,
  eventKey,
  fieldInstance,
  onValueChange,
  value,
}: RegrelloDataGridV2EditableCellProps<RegrelloEditableCellPhoneProps>) {
  const editableCellRef = useRef<HTMLDivElement>(null);
  const [internalValue, setInternalValue] = useState<PhoneFieldPluginFrontendValue>(value);

  const { showToast } = useToastMessageQueue();

  if (fieldInstance.spectrumFieldVersion == null) {
    throw new Error("spectrumFieldVersioIn is unexpectedly not defined");
  }

  const handleValueChange = useCallback(
    (value: PhoneFieldPluginFrontendValue) => {
      // Show the user's exact value in the editable cell.
      setInternalValue(value);

      // But only emit a validated value. This allows us to avoid saving invalid values for Tables
      // Alpha.
      //
      // See: https://regrello.slack.com/archives/C05NGEBLE1E/p1759863355149869
      const validOrEmptyValue = clearValueIfInvalid(value);
      onValueChange(validOrEmptyValue);
    },
    [clearValueIfInvalid, onValueChange],
  );

  const { columnWidth, onKeyDownCapture } = useDataGridV2EditableCell({ api, eventKey });

  const { inputRef } = useDataGridV2FirstKeystroke({
    eventKey,
    onValueChange: handleValueChange,
    parseEventKey: (ek) => ek,
  });

  return (
    <div ref={editableCellRef} style={{ width: columnWidth }}>
      <RegrelloFormFieldPhone
        autoFocus={true}
        inputClassName="rounded-none"
        inputRef={inputRef}
        isDefaultMarginsOmitted={true}
        onBlur={(event) => {
          const isWithinInput = isWithinElement(
            event.relatedTarget as HTMLElement | null | undefined,
            editableCellRef.current,
          );

          if (!isWithinInput) {
            if (internalValue !== value && internalValue != null) {
              // UX: Show a warning toast for Tables Alpha since this is admittedly weird behavior.
              showToast({
                content: t`"${internalValue}" is not a valid phone number`,
                intent: "warning",
              });
            }
            api.stopEditing();
          }
        }}
        onChange={handleValueChange}
        onKeyDownCapture={onKeyDownCapture}
        size="x-small"
        value={internalValue ?? EMPTY_STRING}
      />
    </div>
  );
}
